import { useCallback, useEffect, useState } from 'react';
import { useCambiosSinGuardar, useSubpantalla } from '../ui/navegacionReact';
import { navegacion } from '../ui/navegacion';
import { ClipboardCheck, ArrowLeft, ScanLine } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import ListaEstandar from '../ui/ListaEstandar';
import VistaDetalle from '../ui/VistaDetalle';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import { pitidoOk, pitidoError } from '../lib/pitido';
import { anotadosDe, sumarLocal, anotarTotal } from '../lib/colaConteos';
import { useAvisos } from '../lib/contextoAvisos';
import ModalConfirmar from '../ui/ModalConfirmar';
import BotonExportar from '../ui/BotonExportar';
import { ESTADO_CONTEO, ESTADO_RENGLON } from '../ui/estados';
import { useDatosDeTrabajo } from '../lib/useDatosDeTrabajo';
import ModalAsignar, { ChipAsignado } from '../ui/Asignar';
import AvisoCopiaLocal from '../ui/AvisoCopiaLocal';
import { Cargando } from '../ui/EstadoDePantalla';

import { aCosto, hayCosto, numeroDeCosto, SIN_PERMISO_COSTO_CORTO } from '../lib/costo';
const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

const DETALLE_CONTEO = 'Las cantidades capturadas quedan guardadas en este dispositivo.';

export default function Conteos({ contexto }) {
  const [abierta, setAbierta] = useState(contexto?.id_conteo || null);
  useSubpantalla('id_conteo', !!abierta, () => setAbierta(null), { valor: abierta, abrir: setAbierta, enUrl: true });
  return abierta
    ? <Detalle id={abierta} onVolver={() => setAbierta(null)} />
    : <Listado onAbrir={setAbierta} />;
}

// ------------------------------------------------------------- listado
function Listado({ onAbrir }) {
  const { tieneNivel } = useAuth();
  const [sesiones, setSesiones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [nueva, setNueva] = useState(false);
  const [asignar, setAsignar] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    return api.get('/conteos')
      .then(setSesiones)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    let vigente = true;
    api.get('/conteos')
      .then((d) => vigente && setSesiones(d))
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, []);

  const columnas = [
    { etiqueta: 'Folio', valor: (s) => s.id, clase: 'font-mono text-xs' },
    { etiqueta: 'Qué se contó', valor: (s) => `${QUE_SE_CUENTA[s.alcance] || s.alcance}: ${s.objetivo}`, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Avance',
      valor: (s) => `${s.exactitud?.renglones_contados ?? 0}/${s.exactitud?.renglones_totales ?? 0}`,
      clase: 'tabular-nums text-gray-600',
    },
    {
      etiqueta: 'Exactitud', alineado: 'derecha',
      valor: (s) => s.exactitud?.exactitud_piezas ?? 0,
      render: (s) => <Exactitud valor={s.exactitud?.exactitud_piezas} contados={s.exactitud?.renglones_contados} />,
    },
    {
      etiqueta: 'Faltante', alineado: 'derecha',
      valor: (s) => Number(s.exactitud?.valor_faltante ?? 0),
      render: (s) => (Number(s.exactitud?.valor_faltante) > 0
        ? <span className="text-red-700 font-semibold">{pesos.format(s.exactitud.valor_faltante)}</span>
        : '—'),
      clase: 'tabular-nums',
    },
    {
      // A QUIEN LE TOCA, en la lista y no escondido en la ficha: si hay
      // que abrir cada hoja para saber si tiene dueño, nadie lo mira y la
      // asignacion deja de servir para repartir el turno.
      etiqueta: 'A quién le toca', valor: (s) => s.asignado || 'sin asignar',
      render: (s) => <ChipAsignado nombre={s.asignado} idAsignado={s.id_asignado} />,
    },
    { etiqueta: 'Abierto', tipo: 'fecha_hora', valor: (s) => s.creado_en, render: (s) => <TiempoRelativo valor={s.creado_en} /> },
    { etiqueta: 'Estado', valor: (s) => s.estado, render: (s) => <ChipEstado mapa={ESTADO_CONTEO} clave={s.estado} /> },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Conteos cíclicos"
        subtitulo="Contar poco y seguido es lo que descubre lo que falta antes de que sea tarde."
        nota={
          <NotaExplicativa titulo="Cómo funciona un conteo, y por qué no toca el inventario a mano">
            Al empezar, TEGO guarda una <strong>foto de las cantidades</strong> que tiene en ese momento. Tú
            cuentas lo que de verdad hay; el almacén no se detiene mientras tanto, y al terminar verás qué se
            movió durante la cuenta para explicar cualquier diferencia. Cada diferencia queda como un
            <strong> ajuste registrado</strong> —el saldo nunca se reescribe a mano— y si vale mucho, ese ajuste
            espera el visto bueno de una segunda persona, como cualquier otro movimiento.
          </NotaExplicativa>
        }
        accionPrincipal={tieneNivel('ENCARGADO') ? { etiqueta: 'Nuevo conteo', onClick: () => setNueva(true) } : null}
        columnas={columnas}
        filas={sesiones}
        cargando={cargando}
        error={error}
        onReintentar={() => { setError(null); cargar(); }}
        exportar={{ nombre: 'conteos', columnas }}
        vacio={
          <EstadoVacio
            icono={ClipboardCheck}
            titulo="Todavía no has hecho ningún conteo"
            descripcion="Empieza por un pasillo de alto valor. Contar una zona pequeña cada semana descubre más que un inventario general al año."
          />
        }
        acciones={(s) => [
          { etiqueta: s.estado === 'ABIERTA' ? 'Continuar' : 'Ver', onClick: () => onAbrir(s.id) },
          // «Tomar» y «Asignar» son la misma accion con distinto nombre
          // segun quien mire: el operador toma, el encargado reparte.
          ...(s.estado === 'ABIERTA'
            ? [{ etiqueta: tieneNivel('ENCARGADO') ? 'Asignar' : 'Tomar',
                 onClick: () => setAsignar(s) }]
            : []),
        ]}
      />

      {/* El fallo de carga lo pinta la lista (motivo en español + reintentar). */}
      {nueva && <ModalNuevo onCerrar={() => setNueva(false)} onCreado={(s) => { setNueva(false); cargar(); onAbrir(s.id); }} />}
      {asignar && (
        <ModalAsignar
          ruta={`/conteos/${asignar.id}/asignar`}
          rolMinimo="OPERADOR"
          que="la hoja de conteo"
          actual={asignar.id_asignado}
          nombreActual={asignar.asignado}
          onCerrar={() => setAsignar(null)}
          onListo={() => { setAsignar(null); cargar(); }}
        />
      )}
    </>
  );
}

function Exactitud({ valor, contados }) {
  if (!contados) return <span className="text-gray-500">sin contar</span>;
  const v = Number(valor ?? 0);
  const color = v >= 99 ? 'text-green-700' : v >= 95 ? 'text-amber-700' : 'text-red-700';
  return <span className={`font-semibold tabular-nums ${color}`}>{v}%</span>;
}

// ANEXO-CLIENTE-01 H-2 (20 sep 2026): «UBICACION», «CATEGORIA» y «CLASE_ABC»
// son nombres del sistema. En pantalla se dice qué se contó.
const QUE_SE_CUENTA = {
  UBICACION: 'Una ubicación',
  CATEGORIA: 'Una categoría',
  CLASE_ABC: 'Una clase ABC',
};

function ModalNuevo({ onCerrar, onCreado }) {
  const [alcance, setAlcance] = useState('UBICACION');
  const [ubicacion, setUbicacion] = useState('');
  const [categoria, setCategoria] = useState('');
  const [claseAbc, setClaseAbc] = useState('A');
  const [ubicaciones, setUbicaciones] = useState([]);

  useEffect(() => {
    let vigente = true;
    api.get('/ubicaciones')
      .then((u) => vigente && setUbicaciones(u))
      .catch(() => vigente && setUbicaciones([]));
    return () => { vigente = false; };
  }, []);

  const objetivo = { UBICACION: ubicacion, CATEGORIA: categoria, CLASE_ABC: claseAbc }[alcance];

  return (
    <ModalConfirmar
      titulo="Nuevo conteo"
      descripcion="Guardamos una foto de las cantidades que el sistema tiene ahora. Tú cuentas lo que de verdad hay y al final te mostramos las diferencias."
      extra={
        <div className="space-y-3">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">¿Qué vas a contar?</span>
            <select value={alcance} onChange={(e) => setAlcance(e.target.value)} className="entrada">
              <option value="UBICACION">Una ubicación (un pasillo, un estante)</option>
              <option value="CATEGORIA">Una categoría de producto</option>
              <option value="CLASE_ABC">Una clase ABC</option>
            </select>
          </label>

          {alcance === 'UBICACION' && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">Ubicación</span>
              <select value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} className="entrada">
                <option value="">Selecciona…</option>
                {ubicaciones.map((u) => <option key={u.codigo} value={u.codigo}>{u.codigo} — {u.nombre}</option>)}
              </select>
            </label>
          )}
          {alcance === 'CATEGORIA' && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">Categoría</span>
              <input value={categoria} onChange={(e) => setCategoria(e.target.value)} className="entrada" placeholder="Ej.: FRENOS" />
            </label>
          )}
          {alcance === 'CLASE_ABC' && (
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">Clase</span>
              <select value={claseAbc} onChange={(e) => setClaseAbc(e.target.value)} className="entrada">
                <option value="A">A — lo que más vale, cuéntalo seguido</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
            </label>
          )}
        </div>
      }
      etiquetaConfirmar="Abrir conteo"
      onConfirmar={async () => {
        if (!objetivo) throw new ErrorApi('Elige qué vas a contar.');
        onCreado(await api.post('/conteos', {
          alcance,
          ubicacion: alcance === 'UBICACION' ? ubicacion : null,
          categoria: alcance === 'CATEGORIA' ? categoria : null,
          clase_abc: alcance === 'CLASE_ABC' ? claseAbc : null,
        }));
      }}
      onCerrar={onCerrar}
    />
  );
}

// ------------------------------------------------------------- detalle
const COLS_RENGLONES = [
  { etiqueta: 'SKU', valor: (r) => r.sku },
  { etiqueta: 'Producto', valor: (r) => r.producto },
  { etiqueta: 'Ubicación', valor: (r) => r.ubicacion },
  { etiqueta: 'Lote', valor: (r) => r.codigo_lote || '' },
  { etiqueta: 'Serie', valor: (r) => r.serie || '' },
  { etiqueta: 'Según el sistema', valor: (r) => Number(r.cantidad_teorica) },
  { etiqueta: 'Contado', valor: (r) => (r.cantidad_contada == null ? '' : Number(r.cantidad_contada)) },
  { etiqueta: 'Diferencia', valor: (r) => (r.diferencia == null ? '' : Number(r.diferencia)) },
  { etiqueta: 'Valor diferencia', tipo: 'moneda', valor: (r) => numeroDeCosto(r.valor_diferencia) },
  { etiqueta: 'Estado', valor: (r) => r.estado },
  { etiqueta: 'Contó', valor: (r) => r.contador || '' },
];

function Detalle({ id, onVolver }) {
  const { tieneNivel } = useAuth();
  const [accion, setAccion] = useState(null);
  // LA HOJA SE GUARDA EN EL TELÉFONO. Lo contado sin señal ya sobrevivía a
  // una recarga; la hoja contra la que contar, no — y sin ella el operador
  // se queda en el pasillo con el trabajo delante y la pantalla vacía. Lo
  // que se enseñe de la copia va anunciado con su hora.
  const {
    datos: sesion, error, cargando, momento, recargar,
  } = useDatosDeTrabajo(`/conteos/${id}`);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (cargando && !sesion) return <Cargando filas={4} />;
  if (!sesion) return null;

  const abierta = sesion.estado === 'ABIERTA';
  const puedeCerrar = tieneNivel('ENCARGADO');
  const pendientes = sesion.renglones.filter((r) => r.estado === 'SIN_CONTAR').length;

  return (
    <div className="space-y-3">
      {/* Con cantidades sin mandar, la flecha pregunta igual que atrás. */}
      <button onClick={() => navegacion.conGuardia(onVolver)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-marca)]">
        <ArrowLeft className="h-4 w-4" /> Volver a Conteos
      </button>

      <AvisoCopiaLocal momento={momento} que="La hoja y lo ya contado" />

      <VistaDetalle
        titulo={`Conteo ${sesion.id} · ${sesion.objetivo}`}
        subtitulo={`${QUE_SE_CUENTA[sesion.alcance] || sesion.alcance} · abierto por ${sesion.creador || '—'}`}
        acciones={
          abierta && puedeCerrar
            ? [
                { etiqueta: 'Cerrar y ajustar', onClick: () => setAccion('cerrar'), principal: true },
                { etiqueta: 'Cancelar conteo', onClick: () => setAccion('cancelar') },
              ]
            : []
        }
        encabezado={<Resumen ex={sesion.exactitud} estado={sesion.estado} />}
        pestanas={[
          {
            id: 'renglones',
            etiqueta: `Qué contar${pendientes ? ` (${pendientes} sin contar)` : ''}`,
            render: () => (
              <Renglones sesion={sesion} abierta={abierta} onCapturado={recargar} />
            ),
          },
          {
            id: 'durante',
            etiqueta: `Movimientos durante (${sesion.movimientos_durante.length})`,
            render: () => <Durante lista={sesion.movimientos_durante} />,
          },
        ]}
      />

      {accion && (
        <ModalCierre
          tipo={accion}
          sesion={sesion}
          onCerrar={() => setAccion(null)}
          onListo={() => { setAccion(null); recargar(); }}
        />
      )}
    </div>
  );
}

function Resumen({ ex, estado }) {
  if (!ex) return null;
  const tarjetas = [
    { titulo: 'Avance', valor: `${ex.renglones_contados} / ${ex.renglones_totales}` },
    { titulo: 'Exactitud por posiciones', valor: `${ex.exactitud_renglones}%`, ayuda: 'Cuántas posiciones cuadran' },
    { titulo: 'Exactitud por piezas', valor: `${ex.exactitud_piezas}%`, ayuda: 'Cuántas unidades bailan' },
    { titulo: 'Faltante', valor: aCosto(pesos, ex.valor_faltante), rojo: Number(ex.valor_faltante) > 0,
      ayuda: hayCosto(ex.valor_faltante) ? undefined : SIN_PERMISO_COSTO_CORTO },
    { titulo: 'Sobrante', valor: aCosto(pesos, ex.valor_sobrante) },
  ];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tarjetas.map((t) => (
          <div key={t.titulo} className="bg-white border border-gray-200 rounded-xl p-3">
            <p className="text-[11px] font-semibold text-gray-500 uppercase">{t.titulo}</p>
            <p className={`text-lg font-semibold tabular-nums mt-0.5 ${t.rojo ? 'text-red-700' : 'text-gray-900'}`}>
              {t.valor}
            </p>
            {t.ayuda && <p className="text-[11px] text-gray-500 mt-0.5">{t.ayuda}</p>}
          </div>
        ))}
      </div>
      {estado === 'ABIERTA' && (
        <p className="text-xs text-gray-500">
          Las dos exactitudes miden cosas distintas: cien posiciones perfectas y una con mil piezas de menos
          dan 99% por posiciones y una catástrofe por piezas.
        </p>
      )}
    </div>
  );
}

function Renglones({ sesion, abierta, onCapturado }) {
  const [codigo, setCodigo] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(false);
  const [aCiegas, setACiegas] = useState(false);
  const [ultimo, setUltimo] = useState(null);
  // Lo que se escaneó y no estaba en la hoja, a la espera de que alguien
  // diga si de verdad está delante.
  const [hallazgo, setHallazgo] = useState(null);
  // Lo anotado en el teléfono y todavía sin mandar: {id_renglon: total}.
  const [local, setLocal] = useState({});
  // `false` significa «hay cosas anotadas sin mandar». Se calcula de lo
  // anotado y no de `navigator.onLine`: lo que preocupa al operador no es
  // si hay red ahora mismo, es si su trabajo ya salió del teléfono.
  const sinPendientes = Object.keys(local).length === 0 ? null : false;
  // CONTEO A MEDIAS · TC-DR-AT. Con cantidades anotadas en el teléfono y
  // todavía sin mandar, atrás pregunta antes de dejar la hoja. Lo anotado
  // vive en la cola local (`lib/colaConteos.js`): se conserva aunque se salga.
  useCambiosSinGuardar(() => Object.keys(local).length > 0, { detalle: DETALLE_CONTEO, sub: 'id_conteo' });
  const avisos = useAvisos();

  // Lo que quedó anotado sin señal se recupera al abrir la hoja: si no, el
  // operador vuelve del pasillo, mira la pantalla y ve sus lecturas
  // «perdidas» aunque estén guardadas.
  useEffect(() => {
    let vivo = true;
    anotadosDe(sesion.id).then((a) => vivo && setLocal(a));
    return () => { vivo = false; };
  }, [sesion.id, sesion.renglones]);

  async function escanear(e) {
    e.preventDefault();
    setError(null);
    setHallazgo(null);
    try {
      await api.post(`/conteos/${sesion.id}/capturar`, {
        codigo: codigo.trim(),
        cantidad: Number(cantidad),
      });
      setCodigo('');
      setCantidad('');
      onCapturado();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo capturar.');
      // NO ESTAR EN LA HOJA ES UN HALLAZGO, no un error del operador. El
      // sistema daba ese producto por agotado y hay una caja delante: eso
      // ES el descuadre que se vino a buscar. Se ofrece apuntarlo aquí
      // mismo en vez de mandarlo a registrar una entrada por otro lado,
      // que era decir «que la exactitud de esta hoja no se entere».
      if (err instanceof ErrorApi && err.status === 404) {
        setHallazgo({ codigo: codigo.trim(), cantidad: Number(cantidad) || 1 });
      }
    }
  }

  async function apuntarHallazgo() {
    await api.post(`/conteos/${sesion.id}/hallazgo`, {
      codigo: hallazgo.codigo,
      cantidad: hallazgo.cantidad,
    });
    setHallazgo(null);
    setError(null);
    setCodigo('');
    setCantidad('');
    onCapturado();
  }

  // CONTAR PASANDO EL LECTOR. Es donde más volumen hay de todo el sistema:
  // una recepción son cuarenta referencias, un conteo de almacén son
  // cientos. Teclear cada renglón es lento y es donde se cambia un 12 por
  // un 21.
  //
  // Cada lectura SUMA UNA, y la suma la hace la base (`sumar: true`): con
  // dos personas contando el mismo pasillo desde dos teléfonos, un
  // leer-sumar-escribir en el navegador pierde lecturas sin que nadie se
  // entere — y el conteo saldría cuadrado y mal.
  //
  // El lector se calla mientras se escribe a mano: un lector ES un teclado,
  // y con el cursor en un campo sus dígitos entrarían ahí además de
  // contarse.
  // SIN SEÑAL SE SIGUE CONTANDO. El código se resuelve contra la hoja que
  // ya está cargada en el teléfono —tiene el SKU y la serie de cada
  // renglón— y el total se anota en local. Se manda solo al reconectar, y
  // se manda el TOTAL, que es lo que hace que reenviarlo no cuente de más.
  async function contarSinSenal(leido) {
    const clave = leido.trim().toUpperCase();
    const renglon = sesion.renglones.find(
      (r) => (r.serie || '').toUpperCase() === clave
          || (r.sku || '').toUpperCase() === clave,
    );
    if (!renglon) {
      pitidoError();
      avisos.aviso(`${clave} no está en esta hoja. Sin señal solo se puede `
        + 'contar lo que la hoja ya traía.');
      setUltimo({ sku: clave, error: true });
      return;
    }
    const total = await sumarLocal(sesion.id, renglon.id, 1, renglon.cantidad_contada);
    setLocal((m) => ({ ...m, [renglon.id]: total }));
    pitidoOk();
    setUltimo({ sku: clave, unidades: 1, total, sinSenal: true });
  }

  useEscanerTeclado(
    (leido) => {
      if (!navigator.onLine) { contarSinSenal(leido); return; }
      api.get(`/qr/resolver?codigo=${encodeURIComponent(leido)}`)
        .then((r) => {
          // La caja de doce cuenta doce: el resolutor ya sabe cuántas trae
          // la presentación escaneada.
          const unidades = r.tipo === 'PRODUCTO'
            ? Number(r.presentacion?.unidades || 1) : 1;
          const clave = r.tipo === 'PIEZA' ? r.datos.serie : r.datos.sku;
          return api.post(`/conteos/${sesion.id}/capturar`,
                          { codigo: clave, cantidad: unidades, sumar: true })
            .then((renglon) => {
              pitidoOk();
              setUltimo({ sku: clave, unidades, total: renglon.cantidad_contada });
              onCapturado();
            });
        })
        .catch((err) => {
          pitidoError();
          const msg = err instanceof ErrorApi ? err.message
            : `No se reconoce el código ${leido}.`;
          avisos.aviso(msg);
          setUltimo({ sku: leido, error: true });
        });
    },
    { activo: abierta && !editando },
  );

  return (
    <div className="space-y-3">
      {abierta && (
        <div className={`rounded-xl border p-3 ${
          editando
            ? 'border-gray-200 bg-gray-50 text-gray-500'
            : 'border-[var(--color-marca)] bg-[var(--color-marca-suave)] text-gray-700'}`}>
          <div className="flex items-center gap-2 text-sm">
            <ScanLine className={`h-4 w-4 shrink-0 ${
              editando ? 'text-gray-400' : 'text-[var(--color-marca)]'}`} aria-hidden="true" />
            <span className="font-semibold">
              {editando
                ? 'Lector en pausa mientras escribes'
                : 'Pasa el lector: cada lectura suma una'}
            </span>
          </div>
          {/* `aria-live` para que un lector de pantalla lo cante: a quien
              no oye el pitido, el pitido no le sirve. */}
          <div aria-live="polite" className="mt-1 text-xs">
            {sinPendientes === false && (
              <span className="block text-amber-700 font-semibold mb-0.5">
                Sin señal: lo contado se está guardando en el teléfono y se
                mandará solo al volver la conexión. No cierres la hoja.
              </span>
            )}
            {ultimo ? (
              ultimo.error
                ? <span className="font-semibold text-red-700">
                    {ultimo.sku} — no entra en esta hoja
                  </span>
                : <span className="text-gray-600">
                    Último: <strong>+{ultimo.unidades}</strong> de {ultimo.sku}
                    {ultimo.total != null && ` · llevas ${Number(ultimo.total)}`}
                  </span>
            ) : (
              <span className="text-gray-500">
                Nada leído todavía. También puedes teclear abajo.
              </span>
            )}
          </div>
        </div>
      )}

      {abierta && (
        <label className="flex items-start gap-3">
          <input type="checkbox" checked={aCiegas}
                 onChange={(e) => setACiegas(e.target.checked)} className="mt-1" />
          <span>
            <span className="block text-sm font-semibold text-gray-900">
              Contar a ciegas
            </span>
            <span className="block text-xs text-gray-500">
              Oculta lo que dice el sistema y la diferencia mientras cuentas. Ver lo que
              «debería» haber es lo que hace que se cuente 10 cuando pone 10 —
              y entonces el conteo no comprueba nada.
            </span>
          </span>
        </label>
      )}

      {abierta && (
        <form onSubmit={escanear} className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap gap-2 items-end">
          <label className="flex-1 min-w-[12rem]">
            <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">Escanea o teclea el código</span>
            <input value={codigo} onChange={(e) => setCodigo(e.target.value)}
                   onFocus={() => setEditando(true)} onBlur={() => setEditando(false)}
                   className="entrada" placeholder="SKU o número de serie" autoCapitalize="characters" />
          </label>
          <label className="w-32">
            <span className="block text-xs font-semibold text-gray-500 uppercase mb-1">Cuántos hay</span>
            <input type="number" step="any" min="0" value={cantidad}
                   onChange={(e) => setCantidad(e.target.value)}
                   onFocus={() => setEditando(true)} onBlur={() => setEditando(false)}
                   className="entrada" />
          </label>
          <button type="submit" className="inline-flex items-center gap-1.5 bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] text-white text-sm font-semibold px-3 py-2 rounded-lg">
            <ScanLine className="h-4 w-4" /> Capturar
          </button>
        </form>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{error}</div>}

      {hallazgo && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
          <div className="font-semibold text-amber-900">
            ¿Tienes {hallazgo.codigo} delante?
          </div>
          <p className="text-xs text-amber-800">
            El sistema no lo tenía en este hueco. Si está ahí, apúntalo como
            hallazgo: entra en la hoja con cero según el sistema, cuenta como sobrante
            y al cerrar genera su ajuste. Si no está, no apuntes nada.
          </p>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="w-32">
              <span className="block text-xs font-semibold text-amber-900 mb-1">
                Cuántos hay
              </span>
              <input type="number" step="any" min="0" value={hallazgo.cantidad}
                     onChange={(e) => setHallazgo((h) => ({
                       ...h, cantidad: Number(e.target.value),
                     }))}
                     onFocus={() => setEditando(true)} onBlur={() => setEditando(false)}
                     className="entrada" />
            </label>
            <button type="button" onClick={() => apuntarHallazgo().catch(
              (err) => setError(err.message))}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-sm
                               font-semibold px-3 py-2 rounded-lg">
              Sí, apúntalo
            </button>
            <button type="button" onClick={() => setHallazgo(null)}
                    className="border border-amber-300 text-amber-900 text-sm
                               font-semibold px-3 py-2 rounded-lg">
              No está
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <BotonExportar nombre={`conteo-${sesion.id}`} columnas={COLS_RENGLONES} filas={sesion.renglones} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase border-b border-gray-200">
              <th className="px-4 py-3" scope="col">Producto</th>
              <th className="px-4 py-3" scope="col">Dónde</th>
              {/* El teórico y la diferencia se ocultan al contar a ciegas:
                  son EXACTAMENTE el dato que sesga el conteo. */}
              {!aCiegas && <th className="px-4 py-3 text-right" scope="col">Según el sistema</th>}
              <th className="px-4 py-3 text-right" scope="col">Contado</th>
              {!aCiegas && <th className="px-4 py-3 text-right" scope="col">Diferencia</th>}
              <th className="px-4 py-3" scope="col">Estado</th>
            </tr>
          </thead>
          <tbody>
            {sesion.renglones.map((r) => (
              <Fila key={`${r.id}:${local[r.id] ?? ''}`}
                    r={r} idSesion={sesion.id} abierta={abierta}
                    aCiegas={aCiegas} onCapturado={onCapturado}
                    onEditando={setEditando} local={local[r.id]} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Fila({ r, idSesion, abierta, aCiegas, onCapturado, onEditando, local }) {
  // Lo anotado sin señal manda sobre lo que devolvió el servidor: es más
  // reciente, y esconderlo haría creer que la lectura se perdió.
  const [valor, setValor] = useState(local ?? r.cantidad_contada ?? '');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // Una cantidad escrita que todavía no es la guardada: atrás pregunta.
  const base = local ?? r.cantidad_contada ?? '';
  useCambiosSinGuardar(
    () => abierta && valor !== '' && (base === '' || Number(valor) !== Number(base)),
    { detalle: DETALLE_CONTEO, sub: 'id_conteo' },
  );

  async function guardar() {
    if (valor === '' || Number(valor) < 0) return;
    setGuardando(true);
    setError(null);
    try {
      // Sin señal se anota en el teléfono y se manda al reconectar. Sería
      // absurdo que escanear aguantara el pasillo y escribir no.
      if (!navigator.onLine) {
        await anotarTotal(idSesion, r.id, Number(valor));
        setGuardando(false);
        return;
      }
      await api.post(`/conteos/${idSesion}/renglones/${r.id}`, { cantidad: Number(valor) });
      onCapturado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  const diferencia = r.diferencia == null ? null : Number(r.diferencia);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-2.5">
        <span className="block font-medium text-gray-900">{r.producto}</span>
        <span className="block text-xs font-mono text-gray-500">{r.sku}{r.serie ? ` · ${r.serie}` : ''}</span>
      </td>
      <td className="px-4 py-2.5 text-gray-600">
        {r.ubicacion}{r.codigo_lote ? <span className="text-xs text-gray-500"> · lote {r.codigo_lote}</span> : null}
      </td>
      {!aCiegas && (
        <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
          {Number(r.cantidad_teorica)}
        </td>
      )}
      <td className="px-4 py-2.5 text-right">
        {abierta ? (
          <input
            type="number"
            step="any"
            min="0"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            // Con el foco aquí el lector se calla: sus dígitos entrarían en
            // este campo además de contarse.
            onFocus={() => onEditando?.(true)}
            onBlur={() => { onEditando?.(false); guardar(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
            disabled={guardando}
            placeholder="—"
            className="w-24 text-right border border-gray-300 rounded-lg px-2 py-1 tabular-nums focus:border-[var(--color-marca)] outline-none"
          />
        ) : (
          <span className="tabular-nums">{r.cantidad_contada == null ? '—' : Number(r.cantidad_contada)}</span>
        )}
        {error && <span className="block text-[11px] text-red-600 mt-0.5">{error}</span>}
      </td>
      {!aCiegas && (
      <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${
        diferencia == null ? 'text-gray-500' : diferencia < 0 ? 'text-red-700' : diferencia > 0 ? 'text-amber-700' : 'text-gray-500'
      }`}>
        {diferencia == null ? '—' : (diferencia > 0 ? `+${diferencia}` : diferencia)}
      </td>
      )}
      <td className="px-4 py-2.5">
        <ChipEstado mapa={ESTADO_RENGLON} clave={r.estado} />
        {r.id_pendiente && (
          <span className="block text-[11px] text-amber-700 mt-0.5">ajuste esperando autorización</span>
        )}
      </td>
    </tr>
  );
}

function Durante({ lista }) {
  if (lista.length === 0) {
    return (
      <p className="text-sm text-gray-500 bg-white border border-gray-200 rounded-xl p-6 text-center">
        No se movió nada de estos productos mientras se contaba. Las diferencias que aparezcan no tienen
        esta explicación.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600">
        Esto se movió <strong>mientras contabas</strong>. Si una posición salió sobrante o faltante, mira aquí
        antes de dar por hecho un descuadre.
      </p>
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {lista.map((m) => (
          <div key={m.id} className="px-4 py-2.5 text-sm flex items-center justify-between gap-3">
            <span>
              <span className="font-medium text-gray-900">{m.tipo}</span>{' '}
              <span className="font-mono text-xs text-gray-500">{m.sku}</span>{' '}
              <span className="text-gray-600">
                {Number(m.cantidad)} · {m.ubicacion_origen || '—'} → {m.ubicacion_destino || '—'}
              </span>
            </span>
            <span className="text-xs text-gray-500 shrink-0">
              {m.usuario} · <TiempoRelativo valor={m.fecha} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModalCierre({ tipo, sesion, onCerrar, onListo }) {
  const ex = sesion.exactitud;
  const discrepancias = sesion.renglones.filter((r) => r.estado === 'SOBRANTE' || r.estado === 'FALTANTE').length;
  const sinContar = sesion.renglones.filter((r) => r.estado === 'SIN_CONTAR').length;

  if (tipo === 'cancelar') {
    return (
      <ModalConfirmar
        peligro
        titulo={`Cancelar el conteo ${sesion.id}`}
        descripcion="Se descarta este conteo sin corregir nada. El inventario queda como está."
        pedirMotivo
        etiquetaConfirmar="Cancelar conteo"
        onConfirmar={async (motivo) => { await api.post(`/conteos/${sesion.id}/cancelar`, { nota: motivo }); onListo(); }}
        onCerrar={onCerrar}
      />
    );
  }

  return (
    <ModalConfirmar
      titulo={`Cerrar el conteo ${sesion.id}`}
      descripcion="Cada diferencia quedará como un ajuste registrado en el historial. El saldo nunca se reescribe a mano."
      cambios={[
        { campo: 'Diferencias a corregir', antes: '—', despues: `${discrepancias} posición(es)` },
        { campo: 'Faltante detectado', antes: '—', despues: aCosto(pesos, ex?.valor_faltante) },
        { campo: 'Exactitud por piezas', antes: '—', despues: `${ex?.exactitud_piezas ?? 0}%` },
      ]}
      aviso={
        sinContar > 0
          ? `Quedan ${sinContar} posición(es) sin contar. No se corrigen: se cierran como si no se hubieran revisado, que es lo correcto — pero si esperabas contarlas, hazlo antes.`
          : null
      }
      etiquetaConfirmar="Cerrar y generar ajustes"
      onConfirmar={async (motivo) => { await api.post(`/conteos/${sesion.id}/cerrar`, { nota: motivo || null }); onListo(); }}
      onCerrar={onCerrar}
    />
  );
}
